import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { languageContent } from '../utils/languageContent';
import { supabase } from '../supabase';
import { isGuestUser } from '../utils/guestUtils';
import { dataCache } from '../utils/dataCache';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';
import DeleteAccountModal from '../components/DeleteAccountModal';
import { 
  requestNotificationPermission, 
  scheduleDailyReminder, 
  enableDailyReminder, 
  disableDailyReminder,
  areRemindersEnabled
} from '../utils/notificationUtils';

interface User {
  id: string;
  email: string;
  full_name?: string;
}

interface SettingsScreenProps {
  user: User | null;
  onLogout: () => void;
}

const SettingsScreen: React.FC<SettingsScreenProps> = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { currentLanguage, setCurrentLanguage } = useLanguage();
  const content = languageContent[currentLanguage];
  const [stats, setStats] = React.useState({ streak: 0, xp: 0, lessonsCompleted: 0 });
  const [soundEffectsEnabled, setSoundEffectsEnabled] = React.useState(true);
  const [remindersEnabled, setRemindersEnabled] = React.useState(true);
  const [reminderTime, setReminderTime] = React.useState({ hour: 20, minute: 0 });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editedName, setEditedName] = useState(user?.full_name || user?.email?.split('@')[0] || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [userDisplayName, setUserDisplayName] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [isResettingCache, setIsResettingCache] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  React.useEffect(() => {
    loadStats();
    
    // Load saved language preference
    const savedLanguage = localStorage.getItem('preferredLanguage') as 'bn' | 'it' | 'en';
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
    
    // Load sound effects preference
    const savedSoundPref = localStorage.getItem('soundEffectsEnabled');
    if (savedSoundPref !== null) {
      setSoundEffectsEnabled(savedSoundPref === 'true');
    }

    // Load dark mode preference
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode !== null) {
      const isDarkMode = savedDarkMode === 'true';
      setDarkMode(isDarkMode);
      // Apply dark mode class on mount
      if (isDarkMode) {
        document.documentElement.classList.add('dark-mode');
      }
    }

    // Load reminder preferences
    setRemindersEnabled(areRemindersEnabled());
    const savedReminderTime = localStorage.getItem('dailyReminderTime');
    if (savedReminderTime) {
      try {
        const { hour, minute } = JSON.parse(savedReminderTime);
        setReminderTime({ hour, minute });
      } catch (e) {
        console.error('Error loading reminder time:', e);
      }
    }

    // Load user display name from database
    loadUserDisplayName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Run when user changes

  const loadUserDisplayName = async () => {
    if (isGuestUser()) {
      setUserDisplayName('Guest User');
      setEditedName('Guest User');
      return;
    }

    try {
      // First try to get from database (most up-to-date)
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        const { data: dbUser } = await supabase
          .from('users')
          .select('display_name, full_name')
          .eq('id', authUser.id)
          .single();

        const displayName = dbUser?.display_name || 
                           dbUser?.full_name || 
                           authUser.user_metadata?.full_name ||
                           authUser.user_metadata?.name ||
                           authUser.email?.split('@')[0] || 
                           'User';

        setUserDisplayName(displayName);
        setEditedName(displayName);
      } else {
        // Fallback to user prop
        const fallbackName = user?.full_name || user?.email?.split('@')[0] || 'User';
        setUserDisplayName(fallbackName);
        setEditedName(fallbackName);
      }
    } catch (error) {
      console.error('Error loading user display name:', error);
      // Fallback to user prop
      const fallbackName = user?.full_name || user?.email?.split('@')[0] || 'User';
      setUserDisplayName(fallbackName);
      setEditedName(fallbackName);
    }
  };

  const loadStats = () => {
    try {
      const progressKey = 'userProgress';
      const existingProgress = localStorage.getItem(progressKey);
      const progress = existingProgress ? JSON.parse(existingProgress) : {
        completedLessons: [],
        totalXP: 0,
        streak: 0
      };

      setStats({
        streak: progress.streak || 0,
        xp: progress.totalXP || 0,
        lessonsCompleted: progress.completedLessons?.length || 0,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const switchLanguage = (lang: 'bn' | 'it' | 'en') => {
    setCurrentLanguage(lang);
    localStorage.setItem('preferredLanguage', lang);
    // Reload the page to apply language changes across all screens
    window.location.reload();
  };

  const toggleSoundEffects = () => {
    const newValue = !soundEffectsEnabled;
    setSoundEffectsEnabled(newValue);
    localStorage.setItem('soundEffectsEnabled', String(newValue));
  };

  const toggleDarkMode = () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    localStorage.setItem('darkMode', String(newValue));
    // Apply dark mode class to body
    if (newValue) {
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
    }
  };

  const toggleReminders = async () => {
    if (!remindersEnabled) {
      // Request permission first
      const permission = await requestNotificationPermission();
      if (permission === 'granted') {
        setRemindersEnabled(true);
        await enableDailyReminder();
        await scheduleDailyReminder(reminderTime.hour, reminderTime.minute);
        
        // Detect platform for better message
        const isNative = typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform();
        const platform = isNative ? 'device' : 'browser';
        
        alert(`Reminders enabled! You will receive daily notifications on your ${platform}.`);
      } else {
        const isNative = typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform();
        const settingsPath = isNative 
          ? 'app settings (Android Settings → Apps → Shikhun Italian → Notifications)'
          : 'browser settings';
          
        alert(`Please enable notifications in ${settingsPath}.`);
      }
    } else {
      setRemindersEnabled(false);
      await disableDailyReminder();
    }
  };

  const handleReminderTimeChange = async (hour: number, minute: number) => {
    setReminderTime({ hour, minute });
    if (remindersEnabled) {
      await scheduleDailyReminder(hour, minute);
    }
  };

  const handleSaveProfile = async () => {
    if (isGuestUser()) {
      alert('Guest users cannot edit profile. Please create an account first.');
      return;
    }

    if (!editedName.trim()) {
      alert('Name cannot be empty');
      return;
    }

    setSavingProfile(true);
    try {
      // Update user metadata in Supabase Auth
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          full_name: editedName.trim()
        }
      });

      if (updateError) {
        alert('Error updating profile: ' + updateError.message);
        setSavingProfile(false);
        return;
      }

      // Also update in public.users table
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        const { error: dbError } = await supabase
          .from('users')
          .update({
            display_name: editedName.trim(),
            full_name: editedName.trim()
          })
          .eq('id', currentUser.id);

        if (dbError) {
          console.error('Error updating database:', dbError);
        }
      }

      setIsEditingProfile(false);
      // Reload the display name from database
      await loadUserDisplayName();
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteAccountClick = () => {
    if (isGuestUser()) {
      alert('Guest accounts cannot be deleted. Please sign out instead.');
      return;
    }
    setShowDeleteModal(true);
  };

  const handleDeleteAccountConfirm = async (confirmationText: string) => {
    setDeletingAccount(true);
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        alert('Error: No user found. Please try signing out and signing in again.');
        setDeletingAccount(false);
        return;
      }

      const userId = currentUser.id;

      // 1. Delete user data from database tables
      try {
        // Delete quiz attempts
        await supabase
          .from('QuizAttempts')
          .delete()
          .eq('user_id', userId);

        // Delete user record
        await supabase
          .from('users')
          .delete()
          .eq('id', userId);

        console.log('✅ User data deleted from database');
      } catch (dbError) {
        console.error('Error deleting user data from database:', dbError);
        // Continue with auth deletion even if DB deletion fails
      }

      // 2. Delete user from Supabase Auth
      // Note: Supabase doesn't have a direct delete method, so we'll sign out
      // The user will need to contact support or we can use admin API
      // For now, we'll sign them out and clear all data
      await supabase.auth.signOut();

      // 3. Clear all localStorage data
      localStorage.clear();

      // 4. Show success message and redirect
      setShowDeleteModal(false);
      alert('Your account has been deleted successfully.');
      window.location.href = '/onboarding';
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Error deleting account. Please try again or contact support.');
      setDeletingAccount(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="settings-screen">
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{content.settings}</h1>
        </div>
        <div className="header-btn-placeholder"></div>
      </div>

      <LessonBannerAd />

      <div className="settings-content">
        {/* Profile Section */}
        <div className="settings-profile-card">
          <div className="profile-avatar-medium">
            {(isEditingProfile ? editedName : (userDisplayName || user?.full_name || user?.email || 'U')).charAt(0).toUpperCase()}
          </div>
          <div className="profile-info-compact" style={{ flex: 1 }}>
            {isEditingProfile ? (
              <div style={{ width: '100%' }}>
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder="Enter your name"
                  className="edit-name-input-settings"
                  autoFocus
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    style={{
                      padding: '10px 20px',
                      background: '#e0e5ec',
                      color: '#6B46C1',
                      border: 'none',
                      borderRadius: '12px',
                      fontWeight: '600',
                      cursor: savingProfile ? 'not-allowed' : 'pointer',
                      opacity: savingProfile ? 0.6 : 1,
                      fontSize: '0.9rem',
                      transition: 'all 0.3s ease',
                      boxShadow: savingProfile 
                        ? 'inset 4px 4px 8px rgba(163, 177, 198, 0.5), inset -4px -4px 8px rgba(255, 255, 255, 0.7)'
                        : 'inset 4px 4px 8px rgba(163, 177, 198, 0.5), inset -4px -4px 8px rgba(255, 255, 255, 0.7)'
                    }}
                    onMouseEnter={(e) => {
                      if (!savingProfile) {
                        e.currentTarget.style.boxShadow = 'inset 5px 5px 10px rgba(163, 177, 198, 0.6), inset -5px -5px 10px rgba(255, 255, 255, 0.8)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'inset 4px 4px 8px rgba(163, 177, 198, 0.5), inset -4px -4px 8px rgba(255, 255, 255, 0.7)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    onMouseDown={(e) => {
                      if (!savingProfile) {
                        e.currentTarget.style.boxShadow = 'inset 6px 6px 12px rgba(163, 177, 198, 0.7), inset -6px -6px 12px rgba(255, 255, 255, 0.5)';
                      }
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.boxShadow = 'inset 4px 4px 8px rgba(163, 177, 198, 0.5), inset -4px -4px 8px rgba(255, 255, 255, 0.7)';
                    }}
                  >
                    {savingProfile ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingProfile(false);
                      setEditedName(userDisplayName || user?.full_name || user?.email?.split('@')[0] || '');
                    }}
                    disabled={savingProfile}
                    style={{
                      padding: '10px 20px',
                      background: '#e0e5ec',
                      color: '#666',
                      border: 'none',
                      borderRadius: '12px',
                      fontWeight: '600',
                      cursor: savingProfile ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      transition: 'all 0.3s ease',
                      boxShadow: 'inset 4px 4px 8px rgba(163, 177, 198, 0.5), inset -4px -4px 8px rgba(255, 255, 255, 0.7)'
                    }}
                    onMouseEnter={(e) => {
                      if (!savingProfile) {
                        e.currentTarget.style.boxShadow = 'inset 5px 5px 10px rgba(163, 177, 198, 0.6), inset -5px -5px 10px rgba(255, 255, 255, 0.8)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'inset 4px 4px 8px rgba(163, 177, 198, 0.5), inset -4px -4px 8px rgba(255, 255, 255, 0.7)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    onMouseDown={(e) => {
                      if (!savingProfile) {
                        e.currentTarget.style.boxShadow = 'inset 6px 6px 12px rgba(163, 177, 198, 0.7), inset -6px -6px 12px rgba(255, 255, 255, 0.5)';
                      }
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.boxShadow = 'inset 4px 4px 8px rgba(163, 177, 198, 0.5), inset -4px -4px 8px rgba(255, 255, 255, 0.7)';
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h3>{userDisplayName || user?.full_name || user?.email?.split('@')[0] || 'User'}</h3>
            <p>{user?.email}</p>
                {!isGuestUser() && (
                  <button
                    onClick={() => setIsEditingProfile(true)}
                    style={{
                      marginTop: '10px',
                      padding: '10px 18px',
                      background: '#e0e5ec',
                      color: '#1a1a1a',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.3s ease',
                      boxShadow: 'inset 4px 4px 8px rgba(163, 177, 198, 0.5), inset -4px -4px 8px rgba(255, 255, 255, 0.7)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = 'inset 5px 5px 10px rgba(163, 177, 198, 0.6), inset -5px -5px 10px rgba(255, 255, 255, 0.8)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'inset 4px 4px 8px rgba(163, 177, 198, 0.5), inset -4px -4px 8px rgba(255, 255, 255, 0.7)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.boxShadow = 'inset 6px 6px 12px rgba(163, 177, 198, 0.7), inset -6px -6px 12px rgba(255, 255, 255, 0.5)';
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.boxShadow = 'inset 4px 4px 8px rgba(163, 177, 198, 0.5), inset -4px -4px 8px rgba(255, 255, 255, 0.7)';
                    }}
                  >
                    <span style={{ fontSize: '1rem' }}>✏️</span>
                    <span>Edit Name</span>
                  </button>
                )}
              </>
            )}
          </div>
          <div className="profile-stats-mini">
            <div className="stat-mini">
              <span className="stat-mini-icon">🔥</span>
              <span className="stat-mini-value">{stats.streak}</span>
            </div>
            <div className="stat-mini">
              <span className="stat-mini-icon">⭐</span>
              <span className="stat-mini-value">{stats.xp}</span>
            </div>
            <div className="stat-mini">
              <span className="stat-mini-icon">📚</span>
              <span className="stat-mini-value">{stats.lessonsCompleted}</span>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h3>Learning</h3>
          
          <div className="settings-item">
            <div className="setting-info">
              <div className="setting-title">Language</div>
              <div className="setting-description">Choose your preferred language</div>
            </div>
            <select
              className="language-dropdown-settings"
              value={currentLanguage}
              onChange={(e) => switchLanguage(e.target.value as 'bn' | 'it' | 'en')}
            >
              <option value="bn">বাংলা</option>
              <option value="it">Italiano</option>
              <option value="en">English</option>
            </select>
          </div>

          <div className="settings-item">
            <div className="setting-info">
              <div className="setting-title">Daily Goal</div>
              <div className="setting-description">Set your daily learning target</div>
            </div>
            <div className="setting-value">15 minutes</div>
          </div>

          <div className="settings-item">
            <div className="setting-info">
              <div className="setting-title">Reminders</div>
              <div className="setting-description">Get daily notifications to practice</div>
            </div>
            <div className="setting-toggle">
              <input 
                type="checkbox" 
                checked={remindersEnabled}
                onChange={toggleReminders}
              />
            </div>
          </div>

          {remindersEnabled && (
            <div className="settings-item reminder-time-setting">
              <div className="setting-info">
                <div className="setting-title">Reminder Time</div>
                <div className="setting-description">Set when to receive notifications</div>
              </div>
              <div className="time-picker">
                <input 
                  type="number" 
                  min="0" 
                  max="23" 
                  value={reminderTime.hour}
                  onChange={async (e) => {
                    let hour = parseInt(e.target.value) || 0;
                    // Ensure hour is between 0 and 23
                    hour = Math.max(0, Math.min(23, hour));
                    await handleReminderTimeChange(hour, reminderTime.minute);
                  }}
                  className="time-input"
                />
                <span className="time-separator">:</span>
                <input 
                  type="number" 
                  min="0" 
                  max="59" 
                  value={reminderTime.minute}
                  onChange={async (e) => {
                    let minute = parseInt(e.target.value) || 0;
                    // Ensure minute is between 0 and 59
                    minute = Math.max(0, Math.min(59, minute));
                    await handleReminderTimeChange(reminderTime.hour, minute);
                  }}
                  className="time-input"
                />
              </div>
            </div>
          )}

          <div className="settings-item">
            <div className="setting-info">
              <div className="setting-title">Sound Effects</div>
              <div className="setting-description">Play sounds during lessons</div>
            </div>
            <div className="setting-toggle">
              <input 
                type="checkbox" 
                checked={soundEffectsEnabled}
                onChange={toggleSoundEffects}
              />
            </div>
          </div>

          <div className="settings-item">
            <div className="setting-info">
              <div className="setting-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  style={{ width: '18px', height: '18px' }}
                >
                  {darkMode ? (
                    <>
                      <circle cx="12" cy="12" r="5"></circle>
                      <line x1="12" y1="1" x2="12" y2="3"></line>
                      <line x1="12" y1="21" x2="12" y2="23"></line>
                      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                      <line x1="1" y1="12" x2="3" y2="12"></line>
                      <line x1="21" y1="12" x2="23" y2="12"></line>
                      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                    </>
                  ) : (
                    <>
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                    </>
                  )}
                </svg>
                <span>Dark Mode</span>
              </div>
              <div className="setting-description">Switch to dark theme</div>
            </div>
            <div className="setting-toggle">
              <input 
                type="checkbox" 
                checked={darkMode}
                onChange={toggleDarkMode}
              />
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h3>Account</h3>
          <div className="settings-item">
            <div className="setting-info">
              <div className="setting-title">Email Notifications</div>
              <div className="setting-description">Receive updates via email</div>
            </div>
            <div className="setting-toggle">
              <input type="checkbox" defaultChecked />
            </div>
          </div>

          <div className="settings-item" onClick={() => navigate('/privacy-security')}>
            <div className="setting-info">
              <div className="setting-title">Privacy & Security</div>
              <div className="setting-description">Manage your privacy settings</div>
            </div>
            <div className="setting-arrow">→</div>
          </div>

          <div className="settings-item logout-item" onClick={onLogout}>
            <div className="setting-info">
              <div className="setting-title logout-text">Sign Out</div>
              <div className="setting-description">Log out of your account</div>
            </div>
            <div className="setting-arrow">→</div>
          </div>

          {!isGuestUser() && (
            <div 
              className="settings-item delete-account-item" 
              onClick={handleDeleteAccountClick}
              style={{
                opacity: deletingAccount ? 0.6 : 1,
                cursor: deletingAccount ? 'not-allowed' : 'pointer'
              }}
            >
              <div className="setting-info">
                <div className="setting-title" style={{ color: '#dc3545', fontWeight: '600' }}>
                  Delete Account
                </div>
                <div className="setting-description" style={{ color: '#dc3545' }}>
                  Permanently delete your account and all data
                </div>
              </div>
              <div className="setting-arrow" style={{ color: '#dc3545' }}>→</div>
            </div>
          )}
        </div>

        {/* Delete Account Modal */}
        <DeleteAccountModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
          }}
          onConfirm={handleDeleteAccountConfirm}
          isDeleting={deletingAccount}
        />

        <div className="settings-section">
          <h3>About</h3>
          <div className="settings-item">
            <div className="setting-info">
              <div className="setting-title">Teacher</div>
              <div className="setting-description">
                <div>MD Nahidul Islam</div>
              </div>
            </div>
          </div>

          <div className="settings-item">
            <div className="setting-info">
              <div className="setting-title">Developer</div>
              <div className="setting-description">
                <div>Ronaldo Roy</div>
              </div>
            </div>
          </div>

          <div className="settings-item">
            <div className="setting-info">
              <div className="setting-title">Version</div>
              <div className="setting-description">1.1.5 - Learn Italian</div>
            </div>
          </div>

          <div className="settings-item" onClick={() => navigate('/help-support')}>
            <div className="setting-info">
              <div className="setting-title">Help & Support</div>
              <div className="setting-description">Get help or contact us</div>
            </div>
            <div className="setting-arrow">→</div>
          </div>
        </div>

        <div className="settings-section">
          <h3>Data & Sync</h3>
          <div 
            className="settings-item" 
            onClick={async () => {
              if (isResettingCache) return;
              if (!window.confirm('This will clear all cached data and reload from server. Continue?')) return;
              
              setIsResettingCache(true);
              try {
                // Clear all cache
                dataCache.clearAll();
                
                // Clear sessionStorage cache
                sessionStorage.removeItem('auth_session_check');
                
                // Clear apiCache if available
                try {
                  const { apiCache } = await import('../utils/cache');
                  apiCache.clear();
                } catch (e) {
                  console.log('apiCache not available');
                }
                
                // Force reload the page to reinitialize everything
                alert('Cache cleared! Reloading app...');
                window.location.reload();
              } catch (error) {
                console.error('Error resetting cache:', error);
                alert('Error resetting cache. Please try again.');
                setIsResettingCache(false);
              }
            }}
            style={{ opacity: isResettingCache ? 0.6 : 1 }}
          >
            <div className="setting-info">
              <div className="setting-title" style={{ color: '#667eea' }}>
                {isResettingCache ? 'Resetting...' : '🔄 Reset Cache & Reload'}
              </div>
              <div className="setting-description">
                Clear all cached data and reload from server
              </div>
            </div>
            <div className="setting-arrow">→</div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default SettingsScreen;
